import { LocalCacheInterface } from "./Interfaces/GlobalInterfaces";
import { ExpirationType } from "./LocalStorage";
import { ID_TYPES } from "./Constants/Constants";
import ProfileModel from "../Namespaces/Profile/Models/ProfileModel";
import FootballCountryModel from "../Namespaces/Football/Models/Country/FootballCountryModel";
import ProfileCountryModel from "../Namespaces/Profile/Models/ProfileCountryModel";
import CompetitionBasicModel from "../Namespaces/Football/Models/Competition/CompetitionBasicModel";
import CompetitionFilters from "../Namespaces/Football/Models/Competition/CompetitionFilters";
import ProfileStatsModel from "../Namespaces/Profile/Models/Stats/ProfileStatsModel";
import IdMappingModel from "../IdMapping/Models/IdMappingModel";

const NAME = "FansUnited";
const FANS_UNITED_INITIAL_SCHEMA_V1 = {
  football: {
    ids: [],
    countries: [],
    competitions: [],
    expirations: {
      ids: 0,
    },
  },
  profile: {
    info: {},
    statistics: {},
    badges: null,
    countries: [],
    expirations: {
      profile: 0,
      statistics: 0,
      badges: 0,
    },
  },
  client: {
    features: {},
    expirations: {
      features: 0,
    },
  },
};

interface IAsyncStorage {
  getItem: (key: string) => Promise<any>;
  setItem: (key: string, value: string) => Promise<void>;
}

export default class AsyncStorageCache implements LocalCacheInterface {
  private static instance: AsyncStorageCache;
  private idSchema: string = "";
  private storageInstance: IAsyncStorage = null;

  readonly expirationProfileTimeout: number = 600; // 10 minutes
  readonly expirationBadgesTimeout: number = 3600; // 1 hour
  readonly expirationClientFeaturesTimeout: number = 14_400; // 4 hours
  readonly expirationFootballIdsTimeout: number = 604_800; // 7 days

  private constructor(idSchema: string) {
    this.idSchema = idSchema;
    this.getStorageInstance();
  }

  public static getInstance(idSchema: string): AsyncStorageCache {
    if (!AsyncStorageCache.instance) {
      AsyncStorageCache.instance = new AsyncStorageCache(idSchema);
    }
    return AsyncStorageCache.instance;
  }

  private async getStorageInstance() {
    try {
      // This on web will fallback to an empty module (EmptyModule.js)
      const instance = await import(
        "@react-native-async-storage/async-storage"
      );
      this.storageInstance = instance.default;
    } catch (e) {
      console.error(e);
    }
  }

  private async getData(): Promise<any> {
    try {
      const data = await this.storageInstance.getItem(NAME);

      if (data) {
        return JSON.parse(data);
      } else {
        await this.storageInstance.setItem(
          NAME,
          JSON.stringify(FANS_UNITED_INITIAL_SCHEMA_V1)
        );
        return FANS_UNITED_INITIAL_SCHEMA_V1;
      }
    } catch (error) {
      console.error("AsyncStorage error:", error);
      throw error;
    }
  }

  private async setData(data: any): Promise<void> {
    await this.storageInstance.setItem(NAME, JSON.stringify(data));
  }

  public clearData = async () => {
    await this.setData(FANS_UNITED_INITIAL_SCHEMA_V1);
  };

  public getCompetitions = async (filters?: CompetitionFilters) => {
    const fansUnited = await this.getData();
    if (filters) {
      return filters.competitionSearchFilter(fansUnited.football.competitions);
    }
    return fansUnited.football.competitions;
  };

  public setCompetitions = async (competitions: CompetitionBasicModel[]) => {
    const fansUnited = await this.getData();
    const newCompetitions = [...fansUnited.football.competitions];
    newCompetitions.push(...competitions);
    fansUnited.football.competitions =
      this.makeCompetitionsUnique(newCompetitions);
    await this.setData(fansUnited);
  };

  public getCompetitionsLength = async (): Promise<number> => {
    const data = await this.getData();

    return data.football.competitions.length;
  };

  public getEntityById = async (
    type: string = "",
    id: string = ""
  ): Promise<any> => {
    const data = await this.getData();

    return data.football.ids.find((idObj: any) => {
      const idType = ID_TYPES[this.idSchema];
      return idObj && `${idObj[idType]}` === id && idObj.resource === type;
    });
  };

  public getEntity = async (
    ids: string | string[],
    type: string,
    schema: string
  ) => {
    const data = await this.getData();

    if (Array.isArray(ids)) {
      return data.football.ids.filter((idObj: any) => {
        const idType = ID_TYPES[schema];
        return (
          idObj && ids.includes(`${idObj[idType]}`) && idObj.resource === type
        );
      });
    } else {
      return data.football.ids.find((idObj: any) => {
        const idType = ID_TYPES[schema];
        return idObj && `${idObj[idType]}` === ids && idObj.resource === type;
      });
    }
  };

  public checkForExistingIdObjs = async (idsObj: any, toSchema: string) => {
    const schemaId = ID_TYPES[toSchema];
    let idsChecked = {};

    for (const key of Object.keys(idsObj)) {
      const idsByResource = idsObj[key];
      const foundObjIds = await this.getEntity(idsByResource, key, toSchema);
      const foundIds = foundObjIds.map((objId: any) => objId[schemaId]);
      const missingIds = idsObj[key].filter(
        (id: string) => !foundIds.includes(id)
      );
      idsChecked[key] = idsByResource.map((id: string) =>
        !missingIds.includes(id)
          ? { id: id, exists: true }
          : { id: id, exists: false }
      );
    }

    return idsChecked;
  };

  public getIdsByType = async (
    typeIdsObj: any,
    configSchema: string,
    toSchema: string
  ) => {
    const data = await this.getData();
    const toSchemaId = ID_TYPES[toSchema];
    const configSchemaId = ID_TYPES[configSchema];
    let result = {};

    Object.keys(typeIdsObj).forEach((key: string) => {
      const objIds = typeIdsObj[key];
      const ids: string[] = [];
      data.football.ids.forEach((idObj: any) => {
        objIds.forEach((id: string, idx: number) => {
          if (id === idObj[configSchemaId] && key === idObj.resource) {
            ids[idx] = idObj[toSchemaId];
          }
        });
      });
      result[key] = ids;
    });

    return result;
  };

  public addIdMapping = async (idMapping: IdMappingModel) => {
    const fansUnited = await this.getData();
    fansUnited.football.ids.push(idMapping);
    await this.setData(fansUnited);
  };

  public addIdMappings = async (idMappings: IdMappingModel[]) => {
    const fansUnited = await this.getData();
    let ids = fansUnited.football.ids;
    ids = [...ids, ...idMappings];
    fansUnited.football.ids = ids;
    await this.setData(fansUnited);
  };

  public addTopIdMappings = async (topIdMappings: IdMappingModel[]) => {
    const fansUnited = await this.getData();

    if (fansUnited.football.ids.length) {
      let ids = JSON.parse(JSON.stringify(fansUnited.football.ids));
      ids = ids.filter(
        (idMapping: IdMappingModel) => idMapping.resource !== "match"
      );
      topIdMappings = topIdMappings.filter(
        (idMapping: IdMappingModel) => idMapping.resource === "match"
      );
      ids = [...ids, ...topIdMappings];
      fansUnited.football.ids = ids;
    } else {
      fansUnited.football.ids = topIdMappings;
    }

    fansUnited.football.expirations.ids =
      Math.floor(Date.now() / 1000) + this.expirationFootballIdsTimeout;
    await this.setData(fansUnited);
  };

  public setOwnProfile = async (profile: ProfileModel) => {
    const fansUnited = await this.getData();
    fansUnited.profile.info = profile;
    fansUnited.profile.expirations.profile =
      Math.floor(Date.now() / 1000) + this.expirationProfileTimeout;
    await this.setData(fansUnited);
  };

  public getOwnProfile = async () => {
    const data = await this.getData();

    return data.profile.info;
  };

  public getProfileCountries = async () => {
    const data = await this.getData();

    return data.profile.countries;
  };

  public setProfileCountries = async (countries: ProfileCountryModel[]) => {
    const fansUnited = await this.getData();
    fansUnited.profile.countries = countries;
    await this.setData(fansUnited);
  };

  public getProfileStats = async () => {
    const data = await this.getData();

    return data.profile.statistics;
  };

  public setProfileStats = async (stats: ProfileStatsModel) => {
    const fansUnited = await this.getData();
    fansUnited.profile.statistics = stats;
    fansUnited.profile.expirations.statistics =
      Math.floor(Date.now() / 1000) + this.expirationProfileTimeout;
    await this.setData(fansUnited);
  };

  public getProfileBadges = async () => {
    const data = await this.getData();

    return data.profile.badges;
  };

  public setProfileBadges = async (badges: string[]) => {
    const fansUnited = await this.getData();
    fansUnited.profile.badges = badges;
    fansUnited.profile.expirations.badges =
      Math.floor(Date.now() / 1000) + this.expirationBadgesTimeout;
    await this.setData(fansUnited);
  };

  public getFootballCountries = async () => {
    const data = await this.getData();

    return data.football.countries;
  };

  public setFootballCountries = async (countries: FootballCountryModel[]) => {
    const fansUnited = await this.getData();
    fansUnited.football.countries = countries;
    await this.setData(fansUnited);
  };

  public getClientFeatures = async () => {
    const data = await this.getData();

    return data.client.features;
  };

  public setClientFeatures = async (features: { [key: string]: any }) => {
    const fansUnited = await this.getData();
    const newFeatures = JSON.parse(JSON.stringify(features));
    fansUnited.client.expirations.features =
      Math.floor(Date.now() / 1000) + this.expirationClientFeaturesTimeout;
    fansUnited.client.features = newFeatures;
    await this.setData(fansUnited);
  };

  public deleteClientFeatures = async () => {
    const fansUnited = await this.getData();
    const copyData = JSON.parse(JSON.stringify(fansUnited));
    copyData.client = {
      features: {},
      expirations: {
        features: 0,
      },
    };
    await this.setData(copyData);
  };

  public getExpiration = async (
    namespace: string,
    type: ExpirationType
  ): Promise<number> => {
    await this.initialNewSchema(namespace, "expirations");
    const fansUnited = await this.getData();

    switch (type) {
      case "profile":
        return fansUnited.profile.expirations.profile;
      case "statistics":
        return fansUnited.profile.expirations.statistics;
      case "badges":
        return fansUnited.profile.expirations.badges;
      case "features":
        return fansUnited.client.expirations.features;
      case "ids":
        return fansUnited.football.expirations.ids;
    }
  };

  private makeCompetitionsUnique = (competitions: CompetitionBasicModel[]) => {
    const set = new Set();
    return competitions.filter((competition: CompetitionBasicModel) => {
      const unique = !set.has(competition.id);
      set.add(competition.id);
      return unique;
    });
  };

  private initialNewSchema = async (namespace: string, newField: string) => {
    const fansUnited = await this.getData();

    if (
      !fansUnited ||
      !fansUnited[namespace] ||
      !fansUnited[namespace][newField]
    ) {
      await this.setData(FANS_UNITED_INITIAL_SCHEMA_V1);
    }
  };
}
